import { useState } from "react";
import { ChevronDown, FileDown, FileText, BookOpen, ListChecks, Wrench } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { TEACHER_RESOURCES, type ProjectTopic } from "@/data/endOfYearProject";
import { useToast } from "@/hooks/use-toast";
import { downloadTopicResourcePdf } from "@/lib/topicResourcePdf";

interface Props {
  topic: ProjectTopic;
}

export default function TeacherResourcesPanel({ topic }: Props) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const res = TEACHER_RESOURCES[topic.id];
  if (!res) return null;

  function handleDownloadTxt() {
    const number = String(topic.number).padStart(2, "0");
    const lines: string[] = [];
    lines.push(`ELEMENTPAD - END-OF-YEAR PROJECT`);
    lines.push(`Topic ${number}: ${topic.title}`);
    lines.push(`TEKS ${topic.teks}`);
    lines.push("");
    lines.push("OBJECTIVE");
    lines.push(topic.objective);
    lines.push("");
    lines.push("LESSON STARTER");
    lines.push(res.lessonStarter);
    lines.push("");
    lines.push("INSTRUCTIONS");
    res.instructions.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
    lines.push("");
    lines.push("PATHWAYS");
    topic.pathways.forEach((p) => {
      lines.push(`- [${p.level}] ${p.title}`);
      lines.push(`  ${p.description}`);
    });
    lines.push("");
    lines.push("SUGGESTED TOOLS");
    res.toolNotes.forEach((n) => lines.push(`- ${n}`));
    lines.push("");
    lines.push("LINKS");
    topic.tools.forEach((t) => lines.push(`- ${t.label}: ${t.path}`));
    lines.push("");
    lines.push(`Generated ${new Date().toLocaleString()}`);

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elementpad-topic-${number}-${topic.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Resource downloaded", description: `Topic ${number}: ${topic.title}` });
  }

  function handleDownloadPdf() {
    const ok = downloadTopicResourcePdf(topic);
    if (ok) {
      toast({
        title: "PDF downloaded",
        description: `Topic ${String(topic.number).padStart(2, "0")}: ${topic.title}`,
      });
    } else {
      toast({
        title: "Download failed",
        description: "No resources available for this topic.",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center justify-between w-full text-xs font-semibold uppercase tracking-wide text-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card rounded"
      >
        <span className="flex items-center gap-1.5">
          <BookOpen size={13} aria-hidden="true" />
          Teacher resources
        </span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-3 text-sm">
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Lesson starter
            </p>
            <p className="text-sm text-foreground italic">{res.lessonStarter}</p>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
              <ListChecks size={12} aria-hidden="true" />
              Instructions
            </p>
            <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
              {res.instructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
              <Wrench size={12} aria-hidden="true" />
              Suggested tools
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
              {res.toolNotes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </section>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              size="sm"
              onClick={handleDownloadPdf}
              className="gap-1.5"
            >
              <FileText size={14} aria-hidden="true" />
              Download PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadTxt}
              className="gap-1.5"
            >
              <FileDown size={14} aria-hidden="true" />
              Plain text
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
